import axios from 'axios';

export const API_AUTH_ERROR_EVENT = 'vidaa-api-auth-error';

// Once a 401/403 is seen, every further request on this instance is blocked
// so a denied/expired session can't keep hammering the backend (e.g. /viewers/ohif, /healtz).
let sessionBlocked = false;

export function isApiSessionBlocked() {
  return sessionBlocked;
}

/** @param {Storage | undefined} storage @param {string} key */
function getStorageValue(storage, key) {
  try {
    const value = storage?.getItem(key)?.trim();
    return value && value !== 'undefined' && value !== 'null' ? value : null;
  } catch {
    return null;
  }
}

/** @param {string} name */
function getCookieValue(name) {
  if (typeof document === 'undefined') {
    return null;
  }

  const match = new RegExp(`(?:^|; )${name}=([^;]*)`).exec(document.cookie);
  return match?.[1] ? decodeURIComponent(match[1]).trim() : null;
}

const api = axios.create({
  baseURL: process.env.API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(config => {
  if (typeof window === 'undefined') {
    return config;
  }

  if (sessionBlocked) {
    return Promise.reject(
      Object.assign(new Error('Request blocked: session already flagged as unauthorized.'), {
        code: 'ERR_SESSION_BLOCKED',
      })
    );
  }

  const viewerToken =
    getStorageValue(window.sessionStorage, 'session_viewer') ||
    getStorageValue(window.localStorage, 'session_viewer');
  const sessionId =
    getCookieValue('session_id') || getStorageValue(window.localStorage, 'session_id');

  config.headers = axios.AxiosHeaders.from(config.headers);

  if (viewerToken) {
    config.headers.set('Authorization', `Bearer ${viewerToken.replace(/^Bearer\s+/i, '')}`);
  }

  if (sessionId) {
    config.headers.set('x-api-key', sessionId);
  }

  return config;
});

api.interceptors.response.use(
  response => response,
  error => {
    const status = error.response?.status;

    if ((status === 401 || status === 403) && typeof window !== 'undefined') {
      sessionBlocked = true;

      window.dispatchEvent(
        new CustomEvent(API_AUTH_ERROR_EVENT, {
          detail: {
            status,
            message:
              error.response?.data?.detail ||
              error.response?.data?.message ||
              'Your session has expired.',
          },
        })
      );
    }

    return Promise.reject(error);
  }
);

export default api;
